/**
 * Helpers de normalización y comparación de valores, compartidos entre evaluateMenuRelevant
 * (relevant_transformed) y evaluateConstraint (constraint ODK-like).
 *
 */

export function normalizeFieldToList(value) {
  if (value === null || value === undefined) return [];

  // Si es un arreglo, extraer y normalizar cada elemento, permitiendo CSV dentro de elementos
  if (Array.isArray(value)) {
    return value
      .flatMap(v => {
        if (v === null || v === undefined) return [];
        if (typeof v === 'object') {
          // Intentar extraer propiedades comunes de valor
          const candidates = [];
          if (Object.prototype.hasOwnProperty.call(v, 'config_rpt_valor')) candidates.push(v.config_rpt_valor);
          if (candidates.length === 0) candidates.push(String(v));
          return candidates;
        }
        return [v];
      })
      // Permitir separación por comas o espacios
      .flatMap(x => String(x).trim().split(/[\s,]+/).map(s => s.trim()))
      .filter(s => s.length > 0);
  }

  // Si es string, dividir por separador coma o espacios
  if (typeof value === 'string') {
    return value
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  // Números y booleanos -> lista con su representación string
  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value).trim()];
  }

  // Si es objeto, intentar extraer propiedades conocidas; si no, usar su string
  if (typeof value === 'object') {
    const candidates = [];
    if (Object.prototype.hasOwnProperty.call(value, 'config_rpt_valor')) candidates.push(value.config_rpt_valor);
    if (candidates.length === 0) candidates.push(String(value));

    return candidates
      .flatMap(x => String(x).trim().split(/[\s,]+/).map(s => s.trim()))
      .filter(s => s.length > 0);
  }

  // Cualquier otro tipo -> string único
  return [String(value).trim()].filter(s => s.length > 0);
}

/** Compara igualdad de forma flexible (numérica si ambos son números, si no, string) */
export function equalsFlexible(a, b) {
  const aNum = typeof a === 'number' ? a : (typeof a === 'string' ? Number(a.trim()) : NaN);
  const bNum = typeof b === 'number' ? b : (typeof b === 'string' ? Number(b.trim()) : NaN);
  const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);
  if (bothNumeric) return aNum === bNum;
  return String(a ?? '').trim() === String(b ?? '').trim();
}

/** Convierte a número si es posible, si no retorna NaN */
export function toNumberOrNaN(x) {
  if (typeof x === 'number') return x;
  if (typeof x === 'string') {
    const t = x.trim();
    if (t === '') return NaN;
    const n = Number(t);
    return Number.isNaN(n) ? NaN : n;
  }
  return NaN;
}

export function compareValues(op, left, right) {
  const leftList = normalizeFieldToList(left);
  const rightStr = String(right ?? '').trim();

  if (leftList.length === 0) {
    return false;
  }

  if (leftList.length > 1) {
    if (op === '=' || op === '==') {
      return leftList.some(item => equalsFlexible(item, rightStr));
    }
    if (op === '!=') {
      return leftList.every(item => !equalsFlexible(item, rightStr));
    }
    // Para operadores relacionales con listas, comparar contra cada elemento (true si alguno cumple)
    const rightNum = toNumberOrNaN(rightStr);
    return leftList.some(item => {
      const ln = toNumberOrNaN(item);
      if (Number.isNaN(ln) || Number.isNaN(rightNum)) return false;
      if (op === '<') return ln < rightNum;
      if (op === '>') return ln > rightNum;
      if (op === '<=') return ln <= rightNum;
      if (op === '>=') return ln >= rightNum;
      return false;
    });
  }

  // Escalar
  const leftVal = leftList.length === 1 ? leftList[0] : '';
  if (op === '=' || op === '==') return equalsFlexible(leftVal, rightStr);
  if (op === '!=') return !equalsFlexible(leftVal, rightStr);

  const ln = toNumberOrNaN(leftVal);
  const rn = toNumberOrNaN(rightStr);
  if (Number.isNaN(ln) || Number.isNaN(rn)) {
    // Si no son numéricos, comparar lexicográficamente
    if (op === '<') return String(leftVal) < String(rightStr);
    if (op === '>') return String(leftVal) > String(rightStr);
    if (op === '<=') return String(leftVal) <= String(rightStr);
    if (op === '>=') return String(leftVal) >= String(rightStr);
    return false;
  }
  if (op === '<') return ln < rn;
  if (op === '>') return ln > rn;
  if (op === '<=') return ln <= rn;
  if (op === '>=') return ln >= rn;
  return false;
}

/** Lee el valor "efectivo" de un campo del formulario, resolviendo objetos de respuesta comunes */
export function getField(datos, name) {
  if (!datos || !name) return undefined;
  if (!Object.prototype.hasOwnProperty.call(datos, name)) return undefined;
  const raw = datos[name];
  if (raw === null || raw === undefined) return raw;
  if (Array.isArray(raw)) return raw; // Se maneja en normalizeFieldToList
  if (typeof raw === 'object') {
    // Heurísticas comunes: preferir propiedades de valor reportado
    if (Object.prototype.hasOwnProperty.call(raw, 'config_rpt_valor')) return raw.config_rpt_valor;
    if (Object.prototype.hasOwnProperty.call(raw, 'config_valor')) return raw.config_valor;
    if (Object.prototype.hasOwnProperty.call(raw, 'valor')) return raw.valor;
    if (Object.prototype.hasOwnProperty.call(raw, 'value')) return raw.value;
    // Fallback: retornar el objeto tal cual (normalizeFieldToList lo convertirá)
    return raw;
  }
  return raw;
}
